import { EUROPE_BBOX } from "./geo";
import type { BBox } from "./types";

/** Regular lon/lat grid of 10 m wind. u/v are eastward / northward km/h. */
export type WindField = {
  west: number;
  south: number;
  east: number;
  north: number;
  nx: number;
  ny: number;
  u: number[];
  v: number[];
  observed_at: string;
};

/**
 * ~3.7° × 3.5°. 240 points fit in one GET (~4 kB URL).
 * 900-point POSTs 414 on GET and 502 from the Worker — do not go back there.
 */
export const WIND_GRID_NX = 20;
export const WIND_GRID_NY = 12;

/** Query string for the existing Open-Meteo forecast URL. Browser and Worker share this. */
export function windFieldQuery(box: BBox = EUROPE_BBOX, nx = WIND_GRID_NX, ny = WIND_GRID_NY): URLSearchParams {
  const points = windGridPoints(box, nx, ny);
  const q = new URLSearchParams();
  q.set("latitude", points.map((p) => p.lat.toFixed(3)).join(","));
  q.set("longitude", points.map((p) => p.lon.toFixed(3)).join(","));
  q.set("current", "wind_speed_10m,wind_direction_10m");
  q.set("wind_speed_unit", "kmh");
  q.set("timezone", "UTC");
  q.set("forecast_days", "1");
  return q;
}

type OpenMeteoPoint = {
  current?: { time?: string; wind_speed_10m?: number; wind_direction_10m?: number };
};

export function windFieldFromOpenMeteo(
  body: unknown,
  box: BBox = EUROPE_BBOX,
  nx = WIND_GRID_NX,
  ny = WIND_GRID_NY,
): WindField | null {
  const points = windGridPoints(box, nx, ny);
  const rows = (Array.isArray(body) ? body : [body]) as OpenMeteoPoint[];
  if (rows.length === 0) return null;
  const u = new Array<number>(points.length).fill(0);
  const v = new Array<number>(points.length).fill(0);
  let observed = new Date().toISOString();
  let hits = 0;
  for (let i = 0; i < points.length; i++) {
    const cur = rows[i]?.current;
    const speed = cur?.wind_speed_10m;
    const dir = cur?.wind_direction_10m;
    if (speed == null || dir == null || !Number.isFinite(speed) || !Number.isFinite(dir)) continue;
    const uv = uvFromMeteo(speed, dir);
    u[i] = uv.u;
    v[i] = uv.v;
    hits += 1;
    if (cur?.time) {
      observed = cur.time.endsWith("Z") || cur.time.includes("+") ? new Date(cur.time).toISOString() : new Date(`${cur.time}Z`).toISOString();
    }
  }
  if (hits === 0) return null;
  return { west: box.west, south: box.south, east: box.east, north: box.north, nx, ny, u, v, observed_at: observed };
}

/**
 * Viewport grid: denser when zoomed in, capped at 800 so one Open-Meteo POST stays legal.
 * Pad the box so particles at the screen edge still sample.
 */
export function chooseWindGrid(box: BBox, zoom: number, maxPts = 800): { box: BBox; nx: number; ny: number } {
  const padLon = Math.max(0.4, (box.east - box.west) * 0.12);
  const padLat = Math.max(0.3, (box.north - box.south) * 0.12);
  const padded: BBox = {
    west: box.west - padLon,
    east: box.east + padLon,
    south: box.south - padLat,
    north: box.north + padLat,
  };
  const cell = Math.max(0.15, 6 / 2 ** Math.max(0, zoom - 2.2));
  const spanLon = padded.east - padded.west;
  const spanLat = padded.north - padded.south;
  let nx = Math.max(8, Math.round(spanLon / cell) + 1);
  let ny = Math.max(6, Math.round(spanLat / cell) + 1);
  if (nx * ny > maxPts) {
    const s = Math.sqrt(maxPts / (nx * ny));
    nx = Math.max(8, Math.floor(nx * s));
    ny = Math.max(6, Math.floor(ny * s));
  }
  return { box: padded, nx, ny };
}

export function windGridPoints(box: BBox = EUROPE_BBOX, nx = WIND_GRID_NX, ny = WIND_GRID_NY): Array<{ lon: number; lat: number }> {
  const out: Array<{ lon: number; lat: number }> = [];
  for (let j = 0; j < ny; j++) {
    const lat = box.south + ((box.north - box.south) * j) / Math.max(1, ny - 1);
    for (let i = 0; i < nx; i++) {
      const lon = box.west + ((box.east - box.west) * i) / Math.max(1, nx - 1);
      out.push({ lon, lat });
    }
  }
  return out;
}

/**
 * Meteorological FROM → eastward / northward components.
 * 0° is wind from the north, so the vector points south.
 */
export function uvFromMeteo(speedKmh: number, fromDeg: number): { u: number; v: number } {
  const rad = (fromDeg * Math.PI) / 180;
  return { u: -speedKmh * Math.sin(rad), v: -speedKmh * Math.cos(rad) };
}

export function speedKmh(u: number, v: number): number {
  return Math.hypot(u, v);
}

/** Bilinear sample. Outside the grid is calm so particles die instead of wrapping the Atlantic. */
export function sampleWind(field: WindField, lon: number, lat: number): { u: number; v: number } {
  const { west, south, east, north, nx, ny, u, v } = field;
  if (nx < 2 || ny < 2) return { u: 0, v: 0 };
  const fx = ((lon - west) / (east - west)) * (nx - 1);
  const fy = ((lat - south) / (north - south)) * (ny - 1);
  if (fx < 0 || fy < 0 || fx > nx - 1 || fy > ny - 1) return { u: 0, v: 0 };
  const i0 = Math.floor(fx);
  const j0 = Math.floor(fy);
  const i1 = Math.min(nx - 1, i0 + 1);
  const j1 = Math.min(ny - 1, j0 + 1);
  const tx = fx - i0;
  const ty = fy - j0;
  const idx = (i: number, j: number) => j * nx + i;
  const u00 = u[idx(i0, j0)] ?? 0;
  const u10 = u[idx(i1, j0)] ?? 0;
  const u01 = u[idx(i0, j1)] ?? 0;
  const u11 = u[idx(i1, j1)] ?? 0;
  const v00 = v[idx(i0, j0)] ?? 0;
  const v10 = v[idx(i1, j0)] ?? 0;
  const v01 = v[idx(i0, j1)] ?? 0;
  const v11 = v[idx(i1, j1)] ?? 0;
  return {
    u: u00 * (1 - tx) * (1 - ty) + u10 * tx * (1 - ty) + u01 * (1 - tx) * ty + u11 * tx * ty,
    v: v00 * (1 - tx) * (1 - ty) + v10 * tx * (1 - ty) + v01 * (1 - tx) * ty + v11 * tx * ty,
  };
}

/** Windy-ish speed ramp — dark purple through teal to amber. */
export function windSpeedColor(kmh: number): [number, number, number] {
  const stops: Array<[number, [number, number, number]]> = [
    [0, [18, 12, 48]],
    [8, [42, 28, 110]],
    [18, [28, 72, 150]],
    [32, [12, 122, 122]],
    [48, [36, 150, 72]],
    [70, [196, 186, 28]],
    [95, [230, 118, 20]],
    [130, [210, 36, 36]],
  ];
  if (kmh <= stops[0]![0]) return stops[0]![1];
  for (let i = 1; i < stops.length; i++) {
    const [t1, c1] = stops[i]!;
    const [t0, c0] = stops[i - 1]!;
    if (kmh <= t1) {
      const t = (kmh - t0) / (t1 - t0);
      return [c0[0] + (c1[0] - c0[0]) * t, c0[1] + (c1[1] - c0[1]) * t, c0[2] + (c1[2] - c0[2]) * t];
    }
  }
  return stops[stops.length - 1]![1];
}
